export type StripeCheckoutUiMode = 'embedded' | 'hosted' | 'native_ios'

type StripeCheckoutEnvironment = {
    isPwa: boolean
    userAgent: string
    platform?: string
    maxTouchPoints?: number
}

export type NativeStripePaymentSheetInput = {
    paymentId: number
    paymentIntentClientSecret: string
    publishableKey: string
    successUrl: string
    cancelUrl: string
}

export type NativeStripePaymentSheetResult = {
    status: 'completed' | 'canceled'
}

type NativeStripeBridge = {
    isAvailable(): boolean
    presentPaymentSheet(input: NativeStripePaymentSheetInput): Promise<NativeStripePaymentSheetResult>
}

type StripeCheckoutAction =
    | { type: 'embedded', clientSecret: string }
    | { type: 'redirect', url: string }
    | { type: 'native', paymentId: number, paymentIntentClientSecret: string }
    | { type: 'error' }

type StripeCheckoutSessionLikePayload = {
    payment_id: number
    checkout_ui_mode: StripeCheckoutUiMode
    payment_intent_client_secret: string | null
    checkout_session_client_secret: string | null
    checkout_url: string | null
}

declare global {
    interface Window {
        MrParkitNativeStripe?: NativeStripeBridge
    }
}

export function isIosDevice({
    userAgent,
    platform,
    maxTouchPoints = 0,
}: Pick<StripeCheckoutEnvironment, 'userAgent' | 'platform' | 'maxTouchPoints'>): boolean {
    const normalizedUserAgent = userAgent.toLowerCase()
    const normalizedPlatform = platform?.toLowerCase() ?? ''

    if (/(iphone|ipad|ipod)/i.test(normalizedUserAgent)) {
        return true
    }

    return normalizedPlatform === 'macintel' && maxTouchPoints > 1
}

/**
 * Identifies an iOS device using the PWA routes. `isPwa` comes from the
 * application route, so this intentionally covers both Safari (including a
 * Home Screen web app) and the native iOS wrapper. It does not inspect
 * Safari's standalone/install state.
 */
export function isInstalledIosPwa(environment: StripeCheckoutEnvironment): boolean {
    if (!environment.isPwa) {
        return false
    }

    return isIosDevice(environment)
}

/**
 * Narrows the iOS PWA context to the native WKWebView wrapper. The wrapper
 * appends the PWAShell token to its user agent; Safari and Safari Home Screen
 * web apps do not have this token.
 */
export function isNativeIosAppWrapper(environment: StripeCheckoutEnvironment): boolean {
    return isInstalledIosPwa(environment) && /pwashell/i.test(environment.userAgent)
}

export function getNativeStripeBridge(): NativeStripeBridge | null {
    if (typeof window === 'undefined') {
        return null
    }

    return window.MrParkitNativeStripe ?? null
}

export function hasNativeStripeBridge(): boolean {
    const bridge = getNativeStripeBridge()

    if (!bridge) {
        return false
    }

    try {
        return bridge.isAvailable()
    } catch {
        return false
    }
}

/**
 * Detects the old native wrapper. Current wrappers inject the bridge object at
 * document start even when the native payment handler is temporarily
 * unavailable; old wrappers have PWAShell in their user agent but no bridge.
 */
export function isUnsupportedIosAppWrapper(isPwa: boolean): boolean {
    if (!isPwa || typeof navigator === 'undefined' || typeof window === 'undefined') {
        return false
    }

    return isNativeIosAppWrapper({
        isPwa,
        userAgent: navigator.userAgent,
        platform: navigator.platform,
        maxTouchPoints: navigator.maxTouchPoints,
    }) && window.MrParkitNativeStripe === undefined
}

export function getStripeCheckoutUiModeForCurrentDevice(isPwa: boolean): StripeCheckoutUiMode {
    if (typeof navigator === 'undefined') {
        return 'embedded'
    }

    if (!isNativeIosAppWrapper({
        isPwa,
        userAgent: navigator.userAgent,
        platform: navigator.platform,
        maxTouchPoints: navigator.maxTouchPoints,
    })) {
        return 'embedded'
    }

    return hasNativeStripeBridge() ? 'native_ios' : 'hosted'
}

export async function presentNativeStripePaymentSheet(input: NativeStripePaymentSheetInput): Promise<NativeStripePaymentSheetResult> {
    const bridge = getNativeStripeBridge()

    if (!bridge || !bridge.isAvailable()) {
        throw new Error('Native Stripe bridge unavailable.')
    }

    return await bridge.presentPaymentSheet(input)
}

export function resolveStripeCheckoutAction(payload: StripeCheckoutSessionLikePayload): StripeCheckoutAction {
    if (payload.checkout_ui_mode === 'hosted') {
        return payload.checkout_url
            ? { type: 'redirect', url: payload.checkout_url }
            : { type: 'error' }
    }

    if (payload.checkout_ui_mode === 'native_ios') {
        return payload.payment_intent_client_secret
            ? {
                type: 'native',
                paymentId: payload.payment_id,
                paymentIntentClientSecret: payload.payment_intent_client_secret,
            }
            : { type: 'error' }
    }

    return payload.checkout_session_client_secret
        ? { type: 'embedded', clientSecret: payload.checkout_session_client_secret }
        : { type: 'error' }
}
